import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { atom, useAtom } from 'jotai';
import { v4 } from 'uuid';
export type VerifyVendor = 'yoti' | 'facetec' | 'idv';
export type CaptureType = 'auto' | 'manual';

/**
 * IDV 수행 전체를 연결하는 unique_id를 벤더 화면(yoti/facetec) ↔ IdvFlow 간에도
 * 승계하기 위한 저장소. 두 화면은 AgeVerificationRouter가 서로 다른 컴포넌트 트리로
 * 조건부 렌더링하기 때문에 React state/context만으로는 값을 넘길 수 없다.
 * AgeVerificationRouter unmount 시 다음 인증 세션을 위해 초기화된다.
 */
export const ageVerificationUniqueIdAtom = atom<string | null>(null);
interface AgeVerificationEventContext {
  uniqueId: string;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  verifyVendor: VerifyVendor;
  captureType: CaptureType;
  setCaptureType: (captureType: CaptureType) => void;
  /**
   * 신분증 인증(IDV) 시도 회차. 최초 1, 재시도마다 +1.
   * IDV 외 벤더(Yoti V2/FaceTec)는 사용하지 않는다.
   */
  attemptNo: number;
  setAttemptNo: (updater: (prev: number) => number) => void;
}
const ageVerificationEventContext = createContext<AgeVerificationEventContext | null>(null);
export const useAgeVerificationEventContext = () => {
  const context = useContext(ageVerificationEventContext);
  if (!context) {
    throw new Error('AgeVerificationEventContext not found');
  }
  return context;
};
interface Props {
  verifyVendor: VerifyVendor;
  /**
   * 마운트 시점에 session_id를 곧바로 발급해야 하는 화면(IdvFlow)을 위한 초기값 생성 함수.
   * 지정하지 않으면 기존과 동일하게 'none'(세션 미시작 상태)으로 시작한다.
   */
  initialSessionId?: () => string;
}
export const AgeVerificationEventContextProvider: FC<PropsWithChildren<Props>> = ({
  children,
  verifyVendor,
  initialSessionId
}) => {
  const [persistedUniqueId, setPersistedUniqueId] = useAtom(ageVerificationUniqueIdAtom);
  const uniqueId = useMemo(() => persistedUniqueId ?? v4(), [persistedUniqueId]);
  useEffect(() => {
    if (!persistedUniqueId) setPersistedUniqueId(uniqueId);
  }, [persistedUniqueId, setPersistedUniqueId, uniqueId]);
  const [sessionId, setSessionId] = useState(() => initialSessionId?.() ?? 'none');
  const [captureType, setCaptureType] = useState<CaptureType>('auto');
  const [attemptNo, setAttemptNo] = useState(1);
  return <ageVerificationEventContext.Provider value={{
    uniqueId,
    sessionId,
    setSessionId,
    verifyVendor,
    captureType,
    setCaptureType,
    attemptNo,
    setAttemptNo
  }} data-sentry-element="ageVerificationEventContext.Provider" data-sentry-component="AgeVerificationEventContextProvider" data-sentry-source-file="context.tsx">
      {children}
    </ageVerificationEventContext.Provider>;
};